import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { useAuth } from '@/lib/AuthContext';
import { toast } from 'sonner';
import { User, Send, Loader2, PenLine } from 'lucide-react';
import { cn } from '@/lib/utils';
import ViewW4Modal from './ViewW4Modal';
import CompleteTaxFormModal from '@/components/CompleteTaxFormModal';

const TAX_YEAR = new Date().getFullYear();
const ROLE_LABELS = { owner: 'Owner', super_admin: 'Super Admin', hr: 'HR', accounting: 'Accounting', dispatch: 'Dispatch' };
// Same figure TaxReport.jsx flags 1099-NEC contractors at.
const IRS_1099_THRESHOLD = 600;

// Small compliance status pill -- green when the thing is on file/done,
// amber otherwise.
const ComplianceBadge = ({ ok, okLabel, missingLabel, onClick, clickable, title }) => (
  <span
    onClick={onClick}
    className={cn(
      "text-[10px] font-semibold px-2 py-0.5 rounded-full border flex-shrink-0",
      ok
        ? "text-green-700 dark:text-green-400 bg-green-500/15 dark:bg-green-500/10 border-green-500/40 dark:border-green-500/30"
        : "text-amber-700 dark:text-amber-400 bg-amber-500/15 dark:bg-amber-500/10 border-amber-500/40 dark:border-amber-500/30",
      clickable && "cursor-pointer hover:opacity-80 transition-opacity"
    )}
    title={clickable ? (title || 'Click to toggle') : undefined}
  >
    {ok ? okLabel : missingLabel}
  </span>
);

const YtdBadge = ({ amount, flagThreshold }) => (
  <span className={cn(
    "text-[10px] font-semibold px-2 py-0.5 rounded-full border flex-shrink-0",
    flagThreshold && amount >= flagThreshold
      ? "text-blue-700 dark:text-blue-400 bg-blue-500/15 dark:bg-blue-500/10 border-blue-500/40 dark:border-blue-500/30"
      : "text-muted-foreground bg-muted/40 border-border"
  )}>
    YTD ${amount.toLocaleString('en-US', { maximumFractionDigits: 0 })}
  </span>
);

export default function TaxFormsTab() {
  const { staffRole } = useAuth();
  // W-2 issuance is accounting's call.
  const canToggleW2 = ['hr', 'accounting', 'super_admin'].includes(staffRole);
  const [companyDrivers, setCompanyDrivers] = useState([]);
  const [ownerOperators, setOwnerOperators] = useState([]);
  const [staffMembers, setStaffMembers] = useState([]);
  const [staffW4ByUserId, setStaffW4ByUserId] = useState(new Map());
  const [ytdByEmail, setYtdByEmail] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [viewingW4, setViewingW4] = useState(null); // { personName, w4 } | null
  const [sendingTo, setSendingTo] = useState(null); // email currently being sent a request/reminder
  // A driver/contractor being filled out here on their behalf -- for
  // anyone whose own self-service link is unreachable (e.g. a dual-role
  // account that's ALSO staff: /my-profile always shows the staff view
  // for a staff account, so the emailed link never gets them to the
  // driver-side form), or just faster when accounting has the info in
  // hand (a phone call, a paper form) than waiting on the driver.
  const [fillingOutFor, setFillingOutFor] = useState(null);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [driverData, staff, w4s, settlements] = await Promise.all([
        client.entities.DriverProfile.list('-updated_date', 200),
        client.entities.StaffProfile.filter({ is_active: true }, 'full_name', 200),
        client.entities.StaffW4Form.filter({ tax_year: TAX_YEAR }, '-created_date', 200),
        client.entities.Settlement.list('-settlement_date', 1000),
      ]);
      const seen = new Set();
      const deduped = driverData.filter(d => {
        const key = d.user_email?.toLowerCase() || d.id;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      const activeApproved = deduped.filter(d => d.approval_status === 'Approved' && !d.termination_date);
      setCompanyDrivers(activeApproved.filter(d => d.driver_type !== 'Owner Operator'));
      setOwnerOperators(activeApproved.filter(d => d.driver_type === 'Owner Operator'));
      setStaffMembers(staff.filter(s => s.user_id));
      setStaffW4ByUserId(new Map(w4s.map(w => [w.user_id, w])));

      // YTD gross settlement pay per driver, this tax year -- the same
      // "finalized settlements only" rule TaxReport.jsx's P&L math uses,
      // so this figure and that report's 1099 totals can't disagree.
      const ytd = new Map();
      for (const s of settlements) {
        const dateStr = s.settlement_date || s.finalized_date;
        if (!dateStr || new Date(dateStr).getFullYear() !== TAX_YEAR || !s.is_finalized) continue;
        const key = s.driver_email?.toLowerCase();
        if (!key) continue;
        ytd.set(key, (ytd.get(key) || 0) + (s.final_net_pay ?? s.net_driver_pay ?? 0));
      }
      setYtdByEmail(ytd);
    } finally {
      setLoading(false);
    }
  };

  const toggleW2Driver = async (driver) => {
    if (!canToggleW2) return;
    const key = `w2_issued_${TAX_YEAR}`;
    await client.entities.DriverProfile.update(driver.id, { [key]: !driver[key] });
    loadAll();
  };

  const toggleW2Staff = async (staff) => {
    if (!canToggleW2) return;
    const key = `w2_issued_${TAX_YEAR}`;
    await client.entities.StaffProfile.update(staff.id, { [key]: !staff[key] });
    loadAll();
  };

  // Both a driver's onboarding-captured W-4 fields (on DriverProfile) and
  // a staff member's dedicated StaffW4Form row hold the same information
  // under different field names -- normalize either into the shape
  // ViewW4Modal reads so it doesn't need to know which source it came from.
  const viewDriverW4 = (driver) => setViewingW4({
    personName: driver.full_name,
    w4: {
      name: driver.full_name,
      address: driver.address, city: driver.city, state: driver.state, zip: driver.zip,
      ssn: driver.w4_ssn,
      filingStatus: driver.w4_filing_status,
      exempt: driver.w4_exempt,
      multipleJobs: driver.w4_multiple_jobs,
      dependentsQualifying: driver.w4_dependents_qualifying,
      dependentsOther: driver.w4_dependents_other,
      otherCredits: driver.w4_other_credits,
      otherIncome: driver.w4_other_income,
      deductions: driver.w4_deductions,
      extraWithholding: driver.w4_extra_withholding,
      signature: driver.w4_signature,
    },
  });

  const viewStaffW4 = (staff, w4) => setViewingW4({
    personName: staff.full_name || staff.user_email,
    w4: {
      name: [w4.first_name, w4.middle_initial, w4.last_name].filter(Boolean).join(' '),
      address: w4.address, city: w4.city, state: w4.state, zip: w4.zip,
      ssn: w4.ssn,
      filingStatus: w4.filing_status,
      exempt: w4.exempt,
      multipleJobs: w4.multiple_jobs_checkbox,
      dependentsQualifying: w4.dependents_qualifying_count,
      dependentsOther: w4.dependents_other_count,
      otherCredits: w4.other_credits,
      otherIncome: w4.other_income,
      deductions: w4.deductions,
      extraWithholding: w4.extra_withholding,
      signature: w4.signature,
      signedAt: w4.signed_at,
    },
  });

  // Emails a driver/contractor a direct link to the "Complete your W-4/W-9"
  // banner on their own My Profile page (CompleteTaxFormModal). Staff
  // don't need a link to a specific page -- CompleteW4Gate already blocks
  // their whole app on next login until it's filled in, so their reminder
  // just tells them to sign back in.
  const sendDriverTaxFormRequest = async (driver) => {
    const isOwnerOperator = driver.driver_type === 'Owner Operator';
    const formName = isOwnerOperator ? 'W-9' : 'W-4';
    setSendingTo(driver.user_email);
    try {
      const link = `${window.location.origin}/my-profile`;
      await client.integrations.Core.SendEmail({
        to: driver.user_email,
        subject: `Action needed: complete your ${formName}`,
        body: `<p>Hi ${driver.full_name || 'there'},</p><p>We don't have a ${formName} on file for you yet -- it's required for ${isOwnerOperator ? '1099 reporting' : 'tax withholding on your pay'}.</p><p><a href="${link}">Click here to complete it</a> from your My Profile page. It only takes a couple of minutes.</p>`,
      });
      toast.success(`${formName} request sent to ${driver.user_email}.`);
    } catch (e) {
      toast.error(e?.message || 'Failed to send request.');
    } finally {
      setSendingTo(null);
    }
  };

  const sendStaffW4Reminder = async (staff) => {
    setSendingTo(staff.user_email);
    try {
      const link = window.location.origin;
      await client.integrations.Core.SendEmail({
        to: staff.user_email,
        subject: 'Action needed: complete your W-4',
        body: `<p>Hi ${staff.full_name || 'there'},</p><p>We don't have a W-4 on file for you yet -- it's required for tax withholding on your pay.</p><p><a href="${link}">Sign back in</a> and you'll be prompted to complete it before continuing.</p>`,
      });
      toast.success(`Reminder sent to ${staff.user_email}.`);
    } catch (e) {
      toast.error(e?.message || 'Failed to send reminder.');
    } finally {
      setSendingTo(null);
    }
  };

  const SendRequestButton = ({ email, onSend, label }) => (
    <button
      onClick={onSend}
      disabled={sendingTo === email}
      title={`Email ${email} a link to complete this`}
      className="flex items-center gap-1 px-2 py-0.5 rounded-full border border-border text-[10px] font-medium text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors disabled:opacity-50 flex-shrink-0"
    >
      {sendingTo === email ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} {label}
    </button>
  );

  if (loading) {
    return (
      <div className="glass-card p-12 flex justify-center">
        <div className="w-6 h-6 border-2 border-border border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  const nobody = companyDrivers.length === 0 && ownerOperators.length === 0 && staffMembers.length === 0;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-foreground">W-4, W-9 &amp; W-2/1099 Compliance ({TAX_YEAR})</h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          Company Drivers and Staff are W-2 employees — track their W-4 on file and whether their {TAX_YEAR} W-2 has been issued. Owner Operators are 1099 contractors and use a W-9 instead. YTD figures are gross settlement pay this tax year and are an estimate for your records, not a certified payroll/tax filing figure.
        </p>
      </div>

      {nobody ? (
        <div className="glass-card p-12 text-center">
          <User className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground text-sm">No W-2 employees or 1099 contractors found.</p>
        </div>
      ) : (
        <>
          {companyDrivers.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest mb-2 px-1 text-blue-400">Company Drivers ({companyDrivers.length})</p>
              <div className="glass-card overflow-hidden">
                <div className="divide-y divide-border">
                  {companyDrivers.map(driver => {
                    const ytd = ytdByEmail.get(driver.user_email?.toLowerCase()) || 0;
                    return (
                      <div key={driver.id} className="flex items-center justify-between px-5 py-4 gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-foreground truncate">{driver.full_name}</p>
                          <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                            {driver.employee_number && <p className="text-xs text-muted-foreground">Emp #{driver.employee_number}</p>}
                            {driver.user_email && <p className="text-xs text-muted-foreground">{driver.user_email}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end">
                          <YtdBadge amount={ytd} />
                          <ComplianceBadge
                            ok={!!driver.w4_signature}
                            okLabel="W-4 on file"
                            missingLabel="No W-4 on file"
                            clickable={!!driver.w4_signature}
                            onClick={() => driver.w4_signature && viewDriverW4(driver)}
                            title="Click to view"
                          />
                          {!driver.w4_signature && (
                            <>
                              <button
                                onClick={() => setFillingOutFor(driver)}
                                title="Fill out this driver's W-4 yourself"
                                className="flex items-center gap-1 px-2 py-0.5 rounded-full border border-border text-[10px] font-medium text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors flex-shrink-0"
                              >
                                <PenLine className="w-3 h-3" /> Fill Out
                              </button>
                              {driver.user_email && (
                                <SendRequestButton email={driver.user_email} label="Send Request" onSend={() => sendDriverTaxFormRequest(driver)} />
                              )}
                            </>
                          )}
                          <ComplianceBadge
                            ok={!!driver[`w2_issued_${TAX_YEAR}`]}
                            okLabel={`W-2 issued (${TAX_YEAR})`}
                            missingLabel={`W-2 not issued (${TAX_YEAR})`}
                            clickable={canToggleW2}
                            onClick={() => toggleW2Driver(driver)}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {ownerOperators.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest mb-2 px-1 text-purple-400">1099 Contractors — Owner Operators ({ownerOperators.length})</p>
              <div className="glass-card overflow-hidden">
                <div className="divide-y divide-border">
                  {ownerOperators.map(driver => {
                    const ytd = ytdByEmail.get(driver.user_email?.toLowerCase()) || 0;
                    return (
                      <div key={driver.id} className="flex items-center justify-between px-5 py-4 gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-foreground truncate">{driver.full_name}</p>
                          <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                            {driver.employee_number && <p className="text-xs text-muted-foreground">Emp #{driver.employee_number}</p>}
                            {driver.user_email && <p className="text-xs text-muted-foreground">{driver.user_email}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end">
                          <YtdBadge amount={ytd} flagThreshold={IRS_1099_THRESHOLD} />
                          <ComplianceBadge ok={!!driver.w9_signature} okLabel="W-9 on file" missingLabel="No W-9 on file" />
                          {!driver.w9_signature && (
                            <>
                              <button
                                onClick={() => setFillingOutFor(driver)}
                                title="Fill out this contractor's W-9 yourself"
                                className="flex items-center gap-1 px-2 py-0.5 rounded-full border border-border text-[10px] font-medium text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors flex-shrink-0"
                              >
                                <PenLine className="w-3 h-3" /> Fill Out
                              </button>
                              {driver.user_email && (
                                <SendRequestButton email={driver.user_email} label="Send Request" onSend={() => sendDriverTaxFormRequest(driver)} />
                              )}
                            </>
                          )}
                          <ComplianceBadge
                            ok={!!driver[`f1099_issued_${TAX_YEAR}`]}
                            okLabel={`1099 issued (${TAX_YEAR})`}
                            missingLabel={`1099 not issued (${TAX_YEAR})`}
                            clickable={canToggleW2}
                            onClick={() => canToggleW2 && client.entities.DriverProfile.update(driver.id, { [`f1099_issued_${TAX_YEAR}`]: !driver[`f1099_issued_${TAX_YEAR}`] }).then(loadAll)}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5 px-1">IRS 1099-NEC reporting threshold: ${IRS_1099_THRESHOLD.toLocaleString()}.</p>
            </div>
          )}

          {staffMembers.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest mb-2 px-1 text-orange-400">Staff Members ({staffMembers.length})</p>
              <div className="glass-card overflow-hidden">
                <div className="divide-y divide-border">
                  {staffMembers.map(staff => {
                    const w4 = staffW4ByUserId.get(staff.user_id);
                    return (
                      <div key={staff.id} className="flex items-center justify-between px-5 py-4 gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-semibold text-foreground truncate">{staff.full_name || staff.user_email}</p>
                            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-orange-500/15 dark:bg-orange-500/10 border border-orange-500/40 dark:border-orange-500/30 text-orange-700 dark:text-orange-400 flex-shrink-0">
                              {ROLE_LABELS[staff.staff_role] || staff.staff_role}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                            {staff.employee_number && <p className="text-xs text-muted-foreground">Emp #{staff.employee_number}</p>}
                            {staff.user_email && <p className="text-xs text-muted-foreground">{staff.user_email}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end">
                          <ComplianceBadge
                            ok={!!w4?.signed_at}
                            okLabel="W-4 on file"
                            missingLabel="No W-4 on file"
                            clickable={!!w4?.signed_at}
                            onClick={() => w4?.signed_at && viewStaffW4(staff, w4)}
                            title="Click to view"
                          />
                          {!w4?.signed_at && staff.user_email && (
                            <SendRequestButton email={staff.user_email} label="Send Reminder" onSend={() => sendStaffW4Reminder(staff)} />
                          )}
                          <ComplianceBadge
                            ok={!!staff[`w2_issued_${TAX_YEAR}`]}
                            okLabel={`W-2 issued (${TAX_YEAR})`}
                            missingLabel={`W-2 not issued (${TAX_YEAR})`}
                            clickable={canToggleW2}
                            onClick={() => toggleW2Staff(staff)}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {viewingW4 && (
        <ViewW4Modal
          personName={viewingW4.personName}
          taxYear={TAX_YEAR}
          w4={viewingW4.w4}
          onClose={() => setViewingW4(null)}
        />
      )}

      {fillingOutFor && (
        <CompleteTaxFormModal
          driver={fillingOutFor}
          onClose={() => setFillingOutFor(null)}
          onSaved={loadAll}
        />
      )}
    </div>
  );
}
